import {Component, OnDestroy, OnInit} from '@angular/core';
import {UntypedFormBuilder, UntypedFormControl, UntypedFormGroup, Validators} from '@angular/forms';
import {Title} from '@angular/platform-browser';
import {FixedAssetsService} from '../../../../Services/fixed-assets.service';
import {FixedAssetConfigModel} from '../../../../DataModels/fixed-asset-config-model';
import {DepreciationMethod, ProrateConvention} from '../../../../DataModels/depreciation-models';
import {DataService} from '../../../../Services/data.service';
import {ResponseModel} from '../../../../DataModels/response-model';
import {ActivatedRoute, Router, NavigationEnd} from '@angular/router';
import {filter, takeUntil} from 'rxjs/operators';
import {Subject, take} from 'rxjs';
import {FixedAssetsConfigService} from '../../../../Services/fixed-assets-config.service';
import {Location} from '@angular/common';

@Component({
  selector: 'app-fixed-asset-config',
  templateUrl: './fixed-asset-config.component.html',
  styleUrls: ['./fixed-asset-config.component.css'],
  standalone: false
})
export class FixedAssetConfigComponent implements OnInit, OnDestroy {
  form: UntypedFormGroup;
  methods = Object.values(DepreciationMethod);

  // Header buttons configuration to match other admin forms
  buttonConfig: any = {
    save: true,
    close: true,
    reset: true,
    delete: false,
    saveNew: false,
    saveClose: true,
    print: false,
  };

  // View model consumed by the standard account select modal
  vm: any = {
    accountFilter: {search_string: '', type_name: ''},
    startAccount: 0,
    tempAccountDetailId: '', // repurposed to carry the target form control name
    account: [] as any[],
  };

  private accountLookup = new Map<string, { id: string; number: string; name: string }>();
  // Prevent initial flash of raw ids before lookups are ready
  accountsLoaded = false;

  // Unsaved-changes baseline snapshot
  originalSnapshot: any = {};

  private destroy$ = new Subject<void>();

  constructor(
    private fb: UntypedFormBuilder,
    private titleService: Title,
    private service: FixedAssetsService,
    private faConfigApi: FixedAssetsConfigService,
    private tableDataService: DataService,
    private router: Router,
    private route: ActivatedRoute,
    private location: Location,
  ) {
    this.form = this.fb.group({
      defaultDepreciationMethod: new UntypedFormControl(DepreciationMethod.StraightLine, [Validators.required]),
      defaultUsefulLifeMonths: new UntypedFormControl(60, [Validators.required, Validators.min(1)]),
      assetTagPattern: new UntypedFormControl('FA-{YYYY}-{0001}'),
      // Required defaults – enforce selection via modal before allowing save
      defaultAssetAccountId: new UntypedFormControl('', [Validators.required]),
      defaultAccumulatedDepreciationAccountId: new UntypedFormControl('', [Validators.required]),
      defaultDepreciationExpenseAccountId: new UntypedFormControl('', [Validators.required]),
      defaultGainOnDisposalAccountId: new UntypedFormControl('', [Validators.required]),
      defaultLossOnDisposalAccountId: new UntypedFormControl('', [Validators.required]),
      defaultProceedsSuspenseAccountId: new UntypedFormControl('', [Validators.required]),
      // Prorate options (mutually exclusive checkboxes, stored as a single convention)
      monthlyProrate: new UntypedFormControl(true),
      quarterlyProrate: new UntypedFormControl(false),
      biAnnualProrate: new UntypedFormControl(false),
      yearlyProrate: new UntypedFormControl(false),
      prorateConvention: new UntypedFormControl(ProrateConvention.Monthly),
    });
  }

  ngOnInit(): void {
    this.titleService.setTitle($localize`:Fixed Asset Config@@fixedAssetConfig:Fixed Asset Config`);
    // Load from API (single row config) and patch form once
    this.faConfigApi.get().pipe(take(1)).subscribe(cfg => {
      // Ensure we keep the fetched id in a hidden control so saves include it
      if (!this.form.get('id')) {
        this.form.addControl('id', new UntypedFormControl(''));
      }
      this.form.patchValue({id: (cfg as any)?.id, ...(cfg as any)});
      const conv = (cfg as any).prorateConvention as ProrateConvention | undefined;
      this.setProrateByConvention(conv || (cfg as any)?.monthlyProrate ? ProrateConvention.Monthly : ProrateConvention.Monthly);
      try {
        this.originalSnapshot = this.buildSnapshot();
      } catch {
      }
    });

    // When navigating back to this component (route reuse), re-patch once from latest config
    this.router.events
      .pipe(
        takeUntil(this.destroy$),
        filter(ev => ev instanceof NavigationEnd)
      )
      .subscribe(() => {
        // Ensure we’re on this route then patch from current config value (API)
        this.faConfigApi.get().pipe(take(1)).subscribe(cfg => {
          if (!this.form.get('id')) {
            this.form.addControl('id', new UntypedFormControl(''));
          }
          this.form.patchValue({id: (cfg as any)?.id, ...(cfg as any)});
          const conv = (cfg as any).prorateConvention as ProrateConvention | undefined;
          this.setProrateByConvention(conv || (cfg.monthlyProrate ? ProrateConvention.Monthly : ProrateConvention.Monthly));
          try {
            this.originalSnapshot = this.buildSnapshot();
          } catch {
          }
        });
      });

    // Enforce mutual exclusivity of prorate checkboxes
    const enforce = () => {
      const f = this.form.value;
      if (f.monthlyProrate) {
        this.setProrateByConvention(ProrateConvention.Monthly);
      } else if (f.quarterlyProrate) {
        this.setProrateByConvention(ProrateConvention.Quarterly);
      } else if (f.biAnnualProrate) {
        this.setProrateByConvention(ProrateConvention.BiAnnual);
      } else if (f.yearlyProrate) {
        this.setProrateByConvention(ProrateConvention.Annual);
      } else {
        // default back to Monthly if none selected
        this.setProrateByConvention(ProrateConvention.Monthly);
      }
    };
    this.form.get('monthlyProrate')?.valueChanges.subscribe(() => enforce());
    this.form.get('quarterlyProrate')?.valueChanges.subscribe(() => enforce());
    this.form.get('biAnnualProrate')?.valueChanges.subscribe(() => enforce());
    this.form.get('yearlyProrate')?.valueChanges.subscribe(() => enforce());

    // Load accounts for the modal and display rendering
    this.tableDataService
      .getApiListData('account', '?state=active&pageNumber=0&pageSize=999999')
      .pipe(takeUntil(this.destroy$))
      .subscribe((result: ResponseModel) => {
        const accounts = (result as any)?.Data ?? result;
        this.vm.account = accounts;
        // build lookup for rendering selected account labels
        this.accountLookup = new Map(
          (accounts as any[]).map((a: any) => [String(a.id), {id: String(a.id), number: a.number, name: a.name}])
        );
        this.accountsLoaded = true;
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private setProrateByConvention(conv: ProrateConvention): void {
    // update hidden field
    this.form.get('prorateConvention')?.setValue(conv, {emitEvent: false});
    // set checkboxes exclusively
    this.form.get('monthlyProrate')?.setValue(conv === ProrateConvention.Monthly, {emitEvent: false});
    this.form.get('quarterlyProrate')?.setValue(conv === ProrateConvention.Quarterly, {emitEvent: false});
    this.form.get('biAnnualProrate')?.setValue(conv === ProrateConvention.BiAnnual, {emitEvent: false});
    this.form.get('yearlyProrate')?.setValue(conv === ProrateConvention.Annual, {emitEvent: false});
  }

  onSave(): void {
    if (this.form.invalid) {
      // mirror other admin forms: block save and highlight missing fields
      this.form.markAllAsTouched();
      return;
    }
    const model = this.form.value as any;
    // Ensure we pass through the fetched id if present; else allow service to apply zero-Guid for new
    const payload = {...model, id: model?.id};
    this.faConfigApi.upsert(payload as any).pipe(take(1)).subscribe(() => {
      try {
        this.originalSnapshot = this.buildSnapshot();
      } catch {
      }
    });
  }

  // Header button handlers
  onSaveClose(): void {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }
    const model = this.form.value as any;
    const payload = {...model, id: model?.id};
    this.faConfigApi.upsert(payload as any).pipe(take(1)).subscribe({
      next: () => {
        try {
          this.originalSnapshot = this.buildSnapshot();
        } catch {
        }
        this.onClose();
      },
      error: () => this.onClose() // even on error, leave the page per typical Save Close behavior
    });
  }

  onSaveNew(): void {
    // singleton config – treat as reset
    this.onReset();
  }

  onDelete(): void {
    // not applicable for singleton config
  }

  onReset(): void {
    this.faConfigApi.get().pipe(take(1)).subscribe(cfg => {
      if (!this.form.get('id')) {
        this.form.addControl('id', new UntypedFormControl(''));
      }
      this.form.patchValue({id: (cfg as any)?.id, ...(cfg as any)});
      const conv = (cfg as any).prorateConvention as ProrateConvention | undefined;
      this.setProrateByConvention(conv || (cfg as any)?.monthlyProrate ? ProrateConvention.Monthly : ProrateConvention.Monthly);
      try {
        this.originalSnapshot = this.buildSnapshot();
      } catch {
      }
    });
  }

  onClose(): void {
    try {
      this.location.back();
    } catch {
      this.router.navigate(['/']);
    }
  }

  // Modal wiring
  beginAccountPick(formControlName: keyof FixedAssetConfigModel): void {
    this.vm.tempAccountDetailId = formControlName as string;
  }

  onAccountSelected(accountId: string): void {
    const ctrlName = this.vm.tempAccountDetailId as string;
    if (!ctrlName) {
      return;
    }
    this.form.get(ctrlName)?.setValue(accountId);
  }

  refreshAccounts(): void {
    this.tableDataService
      .getApiListData('account', '?state=active&pageNumber=0&pageSize=999999')
      .subscribe((result: ResponseModel) => {
        const accounts = (result as any)?.Data ?? result;
        this.vm.account = accounts;
        this.accountLookup = new Map(
          (accounts as any[]).map((a: any) => [String(a.id), {id: String(a.id), number: a.number, name: a.name}])
        );
      });
  }

  renderAccount(id: string | null | undefined): string {
    if (!id) {
      return '';
    }
    if (!this.accountsLoaded) {
      return '';
    }
    const a = this.accountLookup.get(String(id));
    return a ? `${a.number} • ${a.name}` : '';
  }

  buildSnapshot(): any {
    try {
      return this.form.getRawValue();
    } catch {
      return {};
    }
  }

  hasChanges(): boolean {
    try {
      const current = this.buildSnapshot();
      const original = this.originalSnapshot || {};
      return JSON.stringify(current) !== JSON.stringify(original);
    } catch {
      return false;
    }
  }

  checkForChanges(): boolean {
    return this.hasChanges();
  }
}
